import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/logger';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Hook Realtime : écoute les changements de statut du profil utilisateur.
 * Si le statut passe à 'rejected', force un signOut immédiat + redirection.
 */
export function useRealtimeAccountStatus(userId: string | undefined) {
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!userId) return;

    // Éviter les doublons
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(`account-status-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const newStatus = (payload.new as any)?.status;
          logger.debug('[REALTIME] user_profiles UPDATE received', { userId, newStatus });

          if (newStatus === 'rejected') {
            logger.warn('[REALTIME] Account suspended, forcing sign out');
            supabase.auth.signOut().then(() => {
              window.location.href = '/login?reason=account_disabled';
            });
          }
        }
      )
      .subscribe((status) => {
        logger.debug('[REALTIME] account-status channel:', status);
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId]);
}
